import {
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { ListPanel } from "./ListPanel";
import { TransferButtons } from "./TransferButtons";
import { MOCK_ACCESS_MODULES } from "./mock";
import { moveNodes, filterTree, sortTree, buildVisibleRows } from "./helpers";

/**
 * DraggableDualList
 *
 * Props:
 *   initialLeft  – array of tree nodes for the left list (defaults to MOCK_ACCESS_MODULES)
 *   initialRight – array of tree nodes for the right list (defaults to [])
 *   labelLeft    – string label for left panel
 *   labelRight   – string label for right panel
 *   onChange     – (leftTree, rightTree) => void
 */
const DraggableDualList = ({
  initialLeft = MOCK_ACCESS_MODULES,
  initialRight = [],
  labelLeft = "Available Modules",
  labelRight = "Selected Modules",
  keepFolderStructure = true,
  onChange,
}) => {
  // Trees
  const [leftTree, setLeftTree] = useState(initialLeft);
  const [rightTree, setRightTree] = useState(initialRight);

  // Selection: Set<id> per list
  const [leftSelected, setLeftSelected] = useState(new Set());
  const [rightSelected, setRightSelected] = useState(new Set());

  // Expanded groups
  const [leftExpanded, setLeftExpanded] = useState(new Set());
  const [rightExpanded, setRightExpanded] = useState(new Set());

  // Search
  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");

  // Pagination
  const [leftPage, setLeftPage] = useState(1);
  const [rightPage, setRightPage] = useState(1);

  // Sort mode per list
  const [leftSort, setLeftSort] = useState(null);
  const [rightSort, setRightSort] = useState(null);

  // Drag-over indicator
  const [dragOverList, setDragOverList] = useState(null);

  // Last clicked id per list (for shift range selection)
  const lastClickedLeft = useRef(null);
  const lastClickedRight = useRef(null);
  // Tracks which list was last interacted with (for Space transfer)
  const lastActiveListRef = useRef(null);

  // ── History (undo/redo) ────────────────────────────────────────────────────
  const historyRef = useRef([{ leftTree: initialLeft, rightTree: initialRight }]);
  const historyIdxRef = useRef(0);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const syncUndoRedo = useCallback(() => {
    setCanUndo(historyIdxRef.current > 0);
    setCanRedo(historyIdxRef.current < historyRef.current.length - 1);
  }, []);

  /** Apply new trees AND push to history. Use instead of setLeftTree/setRightTree. */
  const applyTrees = useCallback((left, right) => {
    // Truncate any future states
    historyRef.current = historyRef.current.slice(0, historyIdxRef.current + 1);
    historyRef.current.push({ leftTree: left, rightTree: right });
    historyIdxRef.current = historyRef.current.length - 1;
    setLeftTree(left);
    setRightTree(right);
    syncUndoRedo();
  }, [syncUndoRedo]);

  const undo = useCallback(() => {
    if (historyIdxRef.current <= 0) return;
    historyIdxRef.current -= 1;
    const { leftTree: l, rightTree: r } = historyRef.current[historyIdxRef.current];
    setLeftTree(l);
    setRightTree(r);
    syncUndoRedo();
  }, [syncUndoRedo]);

  const redo = useCallback(() => {
    if (historyIdxRef.current >= historyRef.current.length - 1) return;
    historyIdxRef.current += 1;
    const { leftTree: l, rightTree: r } = historyRef.current[historyIdxRef.current];
    setLeftTree(l);
    setRightTree(r);
    syncUndoRedo();
  }, [syncUndoRedo]);

  // Keyboard shortcuts: Ctrl+Z = undo | Ctrl+Shift+Z = redo
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && e.shiftKey) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, redo]);

  // Notify parent on tree changes
  useEffect(() => {
    console.log('on change dual list');
    if (onChange) onChange(leftTree, rightTree);
  }, [leftTree, rightTree]);

  // ── Expand toggle ──────────────────────────────────────────────────────────
  const toggleExpand = useCallback((listId, nodeId) => {
    const setter = listId === "left" ? setLeftExpanded : setRightExpanded;
    setter((prev) => {
      const next = new Set(prev);
      next.has(nodeId) ? next.delete(nodeId) : next.add(nodeId);
      return next;
    });
  }, []);

  // ── Selection ──────────────────────────────────────────────────────────────
  /**
   * Build ordered list of all visible node ids for a given tree/expandedIds
   * (used for shift-range selection).
   */
  const getVisibleIds = useCallback((tree, expandedIds, searchQuery, sortMode) => {
    const filtered = filterTree(tree, searchQuery);
    const sorted = sortTree(filtered, sortMode);
    const rows = buildVisibleRows(sorted, expandedIds);
    return rows.map((r) => r.node.id);
  }, []);

  const handleSelect = useCallback(
    (e, node, listId) => {
      const isLeft = listId === "left";
      const selected = isLeft ? leftSelected : rightSelected;
      const setSelected = isLeft ? setLeftSelected : setRightSelected;
      const lastClicked = isLeft ? lastClickedLeft : lastClickedRight;
      const tree = isLeft ? leftTree : rightTree;
      const expanded = isLeft ? leftExpanded : rightExpanded;
      const searchQuery = isLeft ? leftSearch : rightSearch;
      const sortMode = isLeft ? leftSort : rightSort;

      // Track last active list for Space shortcut
      lastActiveListRef.current = listId;

      const visibleIds = getVisibleIds(tree, expanded, searchQuery, sortMode);
      const idx = visibleIds.indexOf(node.id);

      if (e.shiftKey && lastClicked.current !== null) {
        // Range select
        const lastIdx = visibleIds.indexOf(lastClicked.current);
        const [from, to] = [lastIdx, idx].sort((a, b) => a - b);
        const rangeIds = visibleIds.slice(from, to + 1);
        setSelected((prev) => {
          const next = new Set(prev);
          rangeIds.forEach((id) => next.add(id));
          return next;
        });
      } else if (e.ctrlKey || e.metaKey) {
        // Toggle single
        setSelected((prev) => {
          const next = new Set(prev);
          next.has(node.id) ? next.delete(node.id) : next.add(node.id);
          return next;
        });
        lastClicked.current = node.id;
      } else {
        // Single select (clear others)
        setSelected(new Set([node.id]));
        lastClicked.current = node.id;
      }
    },
    [
      leftSelected,
      rightSelected,
      leftTree,
      rightTree,
      leftExpanded,
      rightExpanded,
      leftSearch,
      rightSearch,
      leftSort,
      rightSort,
      getVisibleIds,
    ]
  );

  // ── Drag and drop ──────────────────────────────────────────────────────────
  const dragSourceRef = useRef(null); // { node, listId }

  const handleDragStart = useCallback(
    (e, node, listId) => {
      dragSourceRef.current = { node, listId };
      e.dataTransfer.effectAllowed = "move";
      // If dragged node is not selected, temporarily select it alone
      const selected = listId === "left" ? leftSelected : rightSelected;
      if (!selected.has(node.id)) {
        const setSelected =
          listId === "left" ? setLeftSelected : setRightSelected;
        setSelected(new Set([node.id]));
      }
    },
    [leftSelected, rightSelected]
  );

  const handleDrop = useCallback(
    (e, targetListId) => {
      e.preventDefault();
      setDragOverList(null);
      if (!dragSourceRef.current) return;
      const { listId: sourceListId } = dragSourceRef.current;
      if (sourceListId === targetListId) return;

      const sourceSelected =
        sourceListId === "left" ? leftSelected : rightSelected;
      const sourceTree = sourceListId === "left" ? leftTree : rightTree;
      const targetTree = sourceListId === "left" ? rightTree : leftTree;

      // Move all selected nodes
      const { remaining, added } = moveNodes(sourceTree, targetTree, sourceSelected, keepFolderStructure);

      if (sourceListId === "left") {
        applyTrees(remaining, added);
        setLeftSelected(new Set());
      } else {
        applyTrees(added, remaining);
        setRightSelected(new Set());
      }
      dragSourceRef.current = null;
    },
    [leftSelected, rightSelected, leftTree, rightTree]
  );

  // ── Transfer button handlers ───────────────────────────────────────────────
  const moveSelected = useCallback(
    (fromList) => {
      const isFromLeft = fromList === "left";
      const selected = isFromLeft ? leftSelected : rightSelected;
      if (selected.size === 0) return;
      const sourceTree = isFromLeft ? leftTree : rightTree;
      const targetTree = isFromLeft ? rightTree : leftTree;
      const { remaining, added } = moveNodes(sourceTree, targetTree, selected, keepFolderStructure);
      if (isFromLeft) {
        applyTrees(remaining, added);
        setLeftSelected(new Set());
      } else {
        applyTrees(added, remaining);
        setRightSelected(new Set());
      }
    },
    [leftSelected, rightSelected, leftTree, rightTree]
  );

  // Space = transfer selected from the last active list
  // (declared here, after moveSelected, to avoid temporal dead zone)
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
      if (e.key === " " && lastActiveListRef.current) {
        e.preventDefault();
        moveSelected(lastActiveListRef.current);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [moveSelected]);

  const moveAll = useCallback(
    (fromList) => {
      const isFromLeft = fromList === "left";
      const sourceTree = isFromLeft ? leftTree : rightTree;
      const targetTree = isFromLeft ? rightTree : leftTree;
      // Collect all top-level ids
      const allIds = new Set(sourceTree.map((n) => n.id));
      const { remaining, added } = moveNodes(sourceTree, targetTree, allIds, keepFolderStructure);
      if (isFromLeft) {
        applyTrees(remaining, added);
        setLeftSelected(new Set());
      } else {
        applyTrees(added, remaining);
        setRightSelected(new Set());
      }
    },
    [leftTree, rightTree]
  );

  // ── Double-click transfer ─────────────────────────────────────────────────
  const handleDoubleClick = useCallback(
    (node, listId) => {
      const isFromLeft = listId === "left";
      const selected = isFromLeft ? leftSelected : rightSelected;
      const sourceTree = isFromLeft ? leftTree : rightTree;
      const targetTree = isFromLeft ? rightTree : leftTree;

      // If the double-clicked node is part of the current selection,
      // move all selected nodes; otherwise move only the clicked node.
      const idsToMove = selected.has(node.id) && selected.size > 0
        ? selected
        : new Set([node.id]);

      const { remaining, added } = moveNodes(sourceTree, targetTree, idsToMove, keepFolderStructure);
      if (isFromLeft) {
        applyTrees(remaining, added);
        setLeftSelected(new Set());
      } else {
        applyTrees(added, remaining);
        setRightSelected(new Set());
      }
    },
    [leftSelected, rightSelected, leftTree, rightTree]
  );

  return (
    <div className="flex flex-col gap-3 w-full h-full min-h-0">
      <div className="flex flex-row gap-0 flex-1 min-h-0">
        {/* Left panel */}
        <ListPanel
          title={labelLeft}
          tree={leftTree}
          selectedIds={leftSelected}
          expandedIds={leftExpanded}
          onToggleExpand={(id) => toggleExpand("left", id)}
          onSelect={(e, node) => handleSelect(e, node, "left")}
          onDoubleClick={handleDoubleClick}
          onDragStart={(e, node) => handleDragStart(e, node, "left")}
          onDrop={handleDrop}
          onDragOver={setDragOverList}
          searchQuery={leftSearch}
          onSearchChange={setLeftSearch}
          page={leftPage}
          onPageChange={setLeftPage}
          listId="left"
          isDragOver={dragOverList === "left"}
          sortMode={leftSort}
          onSortChange={(m) => { setLeftSort(m); setLeftPage(1); }}
        />

        {/* Transfer buttons */}
        <TransferButtons
          onAddAll={() => moveAll("left")}
          onAdd={() => moveSelected("left")}
          onRemove={() => moveSelected("right")}
          onRemoveAll={() => moveAll("right")}
          onUndo={undo}
          onRedo={redo}
          canUndo={canUndo}
          canRedo={canRedo}
        />

        {/* Right panel */}
        <ListPanel
          title={labelRight}
          tree={rightTree}
          selectedIds={rightSelected}
          expandedIds={rightExpanded}
          onToggleExpand={(id) => toggleExpand("right", id)}
          onSelect={(e, node) => handleSelect(e, node, "right")}
          onDoubleClick={handleDoubleClick}
          onDragStart={(e, node) => handleDragStart(e, node, "right")}
          onDrop={handleDrop}
          onDragOver={setDragOverList}
          searchQuery={rightSearch}
          onSearchChange={setRightSearch}
          page={rightPage}
          onPageChange={setRightPage}
          listId="right"
          isDragOver={dragOverList === "right"}
          sortMode={rightSort}
          onSortChange={(m) => { setRightSort(m); setRightPage(1); }}
        />
      </div>

      {/* Selection hint */}
      <p className="text-xs text-(--textColor) text-center select-none opacity-40">
        Clique para selecionar · Ctrl+Clique para selecionar múltiplos · Shift+Clique para selecionar intervalo ·
        Arraste itens ou pastas entre as listas
      </p>
    </div>
  );
};

export default DraggableDualList;
